/**
 * ArchitectureTab — Database & Pipeline Reference
 * Visual documentation of the ClickHouse data warehouse architecture
 */
import React, { useState } from 'react';

interface ArchitectureTabProps {
  coinCount: number;
}

/* ── Data constants ── */
const DATABASES = [
  {
    name: 'binance_raw',
    tag: 'Ingestion',
    tagColor: '#58a6ff',
    desc: 'Stores all data exactly as received from Binance API — no cleaning, no transformation.',
    engine: 'ReplacingMergeTree',
    tables: [
      {
        name: 'spot_ohlcv_1m',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Spot candlesticks at 1-minute resolution',
      },
      {
        name: 'spot_ohlcv_5m',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Spot candlesticks at 5-minute resolution',
      },
      {
        name: 'futures_ohlcv_1m',
        type: 'real',
        timeCol: 'open_time',
        desc: 'USDT-M futures at 1-minute resolution',
      },
      {
        name: 'futures_ohlcv_5m',
        type: 'real',
        timeCol: 'open_time',
        desc: 'USDT-M futures at 5-minute resolution',
      },
      {
        name: 'funding_rate',
        type: 'real',
        timeCol: 'funding_time',
        desc: 'Funding rate settlements, every ~8 hours',
      },
      {
        name: 'open_interest',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Total open futures contracts (USD)',
      },
      {
        name: 'global_ls_ratio',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Global long/short account ratio',
      },
      {
        name: 'top_ls_account',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Top traders L/S ratio by account',
      },
      {
        name: 'top_ls_position',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Top traders L/S ratio by position',
      },
      {
        name: 'taker_buy_sell',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Taker buy vs sell volume ratio',
      },
    ],
  },
  {
    name: 'binance_preprocessed',
    tag: 'Features',
    tagColor: '#d29922',
    desc: 'Single source of truth for all derivative/feature data. Each table stores all timeframes via an interval column.',
    engine: 'ReplacingMergeTree',
    tables: [
      {
        name: 'open_interest',
        type: 'real',
        timeCol: 'open_time',
        desc: 'All intervals: 5m, 15m, 30m, 1h, 4h, 1d',
      },
      {
        name: 'global_ls_ratio',
        type: 'real',
        timeCol: 'open_time',
        desc: 'All intervals: 5m, 15m, 30m, 1h, 4h, 1d',
      },
      {
        name: 'top_ls_account',
        type: 'real',
        timeCol: 'open_time',
        desc: 'All intervals: 5m, 15m, 30m, 1h, 4h, 1d',
      },
      {
        name: 'top_ls_position',
        type: 'real',
        timeCol: 'open_time',
        desc: 'All intervals: 5m, 15m, 30m, 1h, 4h, 1d',
      },
      {
        name: 'taker_buy_sell',
        type: 'real',
        timeCol: 'open_time',
        desc: 'All intervals: 5m, 15m, 30m, 1h, 4h, 1d',
      },
      {
        name: 'funding_rate',
        type: 'real',
        timeCol: 'funding_time',
        desc: 'Intervals: 8h (native), 1d (resampled)',
      },
    ],
  },
  {
    name: 'binance_cleaned',
    tag: 'Analytics',
    tagColor: '#3fb950',
    desc: 'Gap-filled and spike-clamped data for production analytics. Derivative tables are views pointing to binance_preprocessed.',
    engine: 'ReplacingMergeTree + Views',
    tables: [
      {
        name: 'spot_ohlcv',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Gap-filled + spike-clamped. All timeframes via interval column',
      },
      {
        name: 'futures_ohlcv',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Gap-filled + spike-clamped. All timeframes via interval column',
      },
      {
        name: 'open_interest',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'global_ls_ratio',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'top_ls_account',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'top_ls_position',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'taker_buy_sell',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'funding_rate',
        type: 'view',
        timeCol: 'funding_time',
        desc: 'View → binance_preprocessed',
      },
    ],
  },
  {
    name: 'binance_spike',
    tag: 'Research',
    tagColor: '#bc8cff',
    desc: 'Identical to binance_cleaned but spikes are preserved. Useful for studying flash crashes and listing pumps.',
    engine: 'ReplacingMergeTree + Views',
    tables: [
      {
        name: 'spot_ohlcv',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Gap-filled, spikes kept. All timeframes',
      },
      {
        name: 'futures_ohlcv',
        type: 'real',
        timeCol: 'open_time',
        desc: 'Gap-filled, spikes kept. All timeframes',
      },
      {
        name: 'open_interest',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'global_ls_ratio',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'top_ls_account',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'top_ls_position',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'taker_buy_sell',
        type: 'view',
        timeCol: 'open_time',
        desc: 'View → binance_preprocessed',
      },
      {
        name: 'funding_rate',
        type: 'view',
        timeCol: 'funding_time',
        desc: 'View → binance_preprocessed',
      },
    ],
  },
];

const PIPELINE_ONBOARD = [
  {
    step: 1,
    name: 'pull_coins.py',
    desc: 'Fetch full 5m OHLCV, 14d of 1m OHLCV, full funding rate, 30d derivatives from Binance API',
    target: 'binance_raw',
  },
  {
    step: 2,
    name: 'Auto-register',
    desc: 'Add coin to TOKENS list in scrape_binance.py (sorted alphabetically)',
    target: 'scrape_binance.py',
  },
  {
    step: 3,
    name: 'pipeline_process_coin.py',
    desc: 'Gap-fill, spike detect/clamp, resample 5m → 15m/30m/1h/4h/1d',
    target: 'cleaned + spike + preprocessed',
  },
];

const PIPELINE_DAILY = [
  {
    step: 1,
    name: 'scrape_binance.py --daily',
    desc: "Fetch yesterday's 1m OHLCV + last 30d derivatives for all tokens",
    target: 'binance_raw',
  },
  {
    step: 2,
    name: 'aggregate_1m_to_5m.py',
    desc: 'Aggregate last 2 days of 1m candles into 5m candles',
    target: 'binance_raw',
  },
  {
    step: 3,
    name: 'pipeline_process_coin.py',
    desc: 'Gap-fill, spike detect/clamp, resample 5m → 15m/30m/1h/4h/1d',
    target: 'cleaned + spike + preprocessed',
  },
];

const USERS = [
  {
    name: 'default',
    role: 'Admin',
    access: 'Full read/write',
    usage: 'Pipeline scripts',
  },
  {
    name: 'analyst',
    role: 'Read-only',
    access: 'SELECT on all 4 databases',
    usage: 'Dashboard / API',
  },
];

export default function ArchitectureTab({ coinCount }: ArchitectureTabProps) {
  const [expandedDb, setExpandedDb] = useState<string | null>('binance_raw');

  const toggleDb = (name: string) =>
    setExpandedDb(expandedDb === name ? null : name);

  return (
    <div className="ar-root">
      {/* ── Header stats ── */}
      <div className="ar-stats-row">
        <div className="ar-stat-card">
          <div className="ar-stat-val">4</div>
          <div className="ar-stat-label">Databases</div>
        </div>
        <div className="ar-stat-card">
          <div className="ar-stat-val">32</div>
          <div className="ar-stat-label">Total Tables</div>
        </div>
        <div className="ar-stat-card">
          <div className="ar-stat-val">{coinCount || 34}</div>
          <div className="ar-stat-label">Coins</div>
        </div>
        <div className="ar-stat-card">
          <div className="ar-stat-val">6</div>
          <div className="ar-stat-label">Timeframes</div>
        </div>
        <div className="ar-stat-card">
          <div className="ar-stat-val">2</div>
          <div className="ar-stat-label">Pipeline Modes</div>
        </div>
      </div>

      <div className="ar-grid">
        {/* ── Left: Databases ── */}
        <div className="ar-col">
          <div className="at-card">
            <div className="at-card-header">
              <span className="at-card-title">Database Layer Architecture</span>
              <span className="ar-tag-sm">ClickHouse</span>
            </div>
            <div className="ar-db-list">
              {DATABASES.map((db) => {
                const isOpen = expandedDb === db.name;
                const realCount = db.tables.filter(
                  (t) => t.type === 'real',
                ).length;
                const viewCount = db.tables.filter(
                  (t) => t.type === 'view',
                ).length;
                return (
                  <div
                    key={db.name}
                    className={`ar-db ${isOpen ? 'open' : ''}`}
                  >
                    <button
                      className="ar-db-header"
                      onClick={() => toggleDb(db.name)}
                    >
                      <div className="ar-db-left">
                        <span className="ar-db-arrow">
                          {isOpen ? '▾' : '▸'}
                        </span>
                        <span className="ar-db-name">{db.name}</span>
                        <span
                          className="ar-db-tag"
                          style={{
                            color: db.tagColor,
                            borderColor: db.tagColor + '44',
                            background: db.tagColor + '15',
                          }}
                        >
                          {db.tag}
                        </span>
                      </div>
                      <div className="ar-db-counts">
                        {realCount > 0 && (
                          <span className="ar-count-pill real">
                            {realCount} tables
                          </span>
                        )}
                        {viewCount > 0 && (
                          <span className="ar-count-pill view">
                            {viewCount} views
                          </span>
                        )}
                      </div>
                    </button>

                    {isOpen && (
                      <div className="ar-db-body">
                        <div className="ar-db-desc">{db.desc}</div>
                        <div className="ar-db-engine">
                          Engine: <span>{db.engine}</span> · Dedup key:{' '}
                          <span>(symbol, {db.tables[0].timeCol})</span>
                        </div>
                        <table className="ar-table">
                          <thead>
                            <tr>
                              <th style={{ width: 28 }}></th>
                              <th>Table</th>
                              <th>Time Column</th>
                              <th>Description</th>
                            </tr>
                          </thead>
                          <tbody>
                            {db.tables.map((t) => (
                              <tr key={t.name}>
                                <td>
                                  <span
                                    className={`ar-type-dot ${t.type}`}
                                    title={t.type === 'view' ? 'View' : 'Table'}
                                  />
                                </td>
                                <td className="ar-table-name">{t.name}</td>
                                <td className="ar-table-col">{t.timeCol}</td>
                                <td className="ar-table-desc">{t.desc}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* ── Data flow ── */}
          <div className="at-card">
            <div className="at-card-header">
              <span className="at-card-title">Data Flow</span>
            </div>
            <div className="at-card-body">
              <div className="ar-flow-diagram">
                {/* Source */}
                <div className="ar-flow-row">
                  <div className="ar-flow-node ar-flow-source">Binance API</div>
                </div>
                <div className="ar-flow-connector">
                  <div className="ar-flow-line" />
                  <div className="ar-flow-split" />
                </div>

                {/* Two paths */}
                <div className="ar-flow-row ar-flow-paths">
                  <div className="ar-flow-path">
                    <div className="ar-flow-badge manual">Manual</div>
                    <div className="ar-flow-node ar-flow-script">
                      pull_coins.py
                    </div>
                    <div className="ar-flow-line-v" />
                    <div className="ar-flow-node ar-flow-script">
                      pipeline_process_coin.py
                    </div>
                  </div>
                  <div className="ar-flow-path">
                    <div className="ar-flow-badge auto">Daily 1AM UTC</div>
                    <div className="ar-flow-node ar-flow-script">
                      scrape_binance.py
                    </div>
                    <div className="ar-flow-line-v" />
                    <div className="ar-flow-node ar-flow-script">
                      aggregate_1m_to_5m.py
                    </div>
                    <div className="ar-flow-line-v" />
                    <div className="ar-flow-node ar-flow-script">
                      pipeline_process_coin.py
                    </div>
                  </div>
                </div>

                {/* Merge */}
                <div className="ar-flow-connector">
                  <div className="ar-flow-merge" />
                  <div className="ar-flow-line" />
                </div>

                {/* Intermediate */}
                <div className="ar-flow-row">
                  <div className="ar-flow-node ar-flow-db raw">binance_raw</div>
                </div>

                <div className="ar-flow-connector">
                  <div className="ar-flow-line" />
                  <div className="ar-flow-split" />
                </div>

                {/* Output databases */}
                <div className="ar-flow-row ar-flow-outputs">
                  <div className="ar-flow-output">
                    <div className="ar-flow-node ar-flow-db cleaned">
                      binance_cleaned
                    </div>
                    <div className="ar-flow-note">Spikes removed</div>
                  </div>
                  <div className="ar-flow-output">
                    <div className="ar-flow-node ar-flow-db spike">
                      binance_spike
                    </div>
                    <div className="ar-flow-note">Spikes kept</div>
                  </div>
                  <div className="ar-flow-output">
                    <div className="ar-flow-node ar-flow-db preprocessed">
                      binance_preprocessed
                    </div>
                    <div className="ar-flow-note">Derivatives / features</div>
                  </div>
                </div>

                {/* Views note */}
                <div className="ar-flow-views-note">
                  Derivative tables in cleaned &amp; spike are{' '}
                  <strong>views</strong> pointing to binance_preprocessed
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Right: Pipelines + Access ── */}
        <div className="ar-col">
          {/* Onboarding pipeline */}
          <div className="at-card">
            <div className="at-card-header">
              <span className="at-card-title">Mode 1: New Coin Onboarding</span>
              <span
                className="ar-tag-sm"
                style={{ color: '#3fb950', borderColor: '#3fb95044' }}
              >
                Manual
              </span>
            </div>
            <div className="at-card-body">
              <div className="ar-pipeline">
                {PIPELINE_ONBOARD.map((s, i) => (
                  <div key={i} className="ar-pipe-step">
                    <div className="ar-pipe-num">{s.step}</div>
                    <div className="ar-pipe-content">
                      <div className="ar-pipe-name">{s.name}</div>
                      <div className="ar-pipe-desc">{s.desc}</div>
                      <div className="ar-pipe-target">→ {s.target}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Daily pipeline */}
          <div className="at-card">
            <div className="at-card-header">
              <span className="at-card-title">
                Mode 2: Daily Incremental Update
              </span>
              <span
                className="ar-tag-sm"
                style={{ color: '#58a6ff', borderColor: '#58a6ff44' }}
              >
                Cron · 1:00 AM UTC
              </span>
            </div>
            <div className="at-card-body">
              <div className="ar-pipeline">
                {PIPELINE_DAILY.map((s, i) => (
                  <div key={i} className="ar-pipe-step">
                    <div className="ar-pipe-num">{s.step}</div>
                    <div className="ar-pipe-content">
                      <div className="ar-pipe-name">{s.name}</div>
                      <div className="ar-pipe-desc">{s.desc}</div>
                      <div className="ar-pipe-target">→ {s.target}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Processing detail */}
          <div className="at-card">
            <div className="at-card-header">
              <span className="at-card-title">Processing Pipeline Detail</span>
            </div>
            <div className="at-card-body">
              <div className="ar-process-grid">
                <div className="ar-process-item">
                  <div className="ar-process-label">Gap Detection</div>
                  <div className="ar-process-val">
                    Forward-fill price, zero volume for missing candles
                  </div>
                </div>
                <div className="ar-process-item">
                  <div className="ar-process-label">Spike Detection</div>
                  <div className="ar-process-val">
                    Wick threshold 5% · Single-bar threshold 5% · Range
                    multiplier 10x · Rolling window 50 bars
                  </div>
                </div>
                <div className="ar-process-item">
                  <div className="ar-process-label">Spike Clamping</div>
                  <div className="ar-process-val">
                    Extreme wicks reduced to body ± 0.1% (cleaned only)
                  </div>
                </div>
                <div className="ar-process-item">
                  <div className="ar-process-label">OHLCV Resampling</div>
                  <div className="ar-process-val">
                    5m → 15m, 30m, 1h, 4h, 1d (O=first, H=max, L=min, C=last,
                    V=sum)
                  </div>
                </div>
                <div className="ar-process-item">
                  <div className="ar-process-label">Feature Resampling</div>
                  <div className="ar-process-val">
                    5m → 15m, 30m, 1h, 4h, 1d (last/sum depending on type)
                  </div>
                </div>
                <div className="ar-process-item">
                  <div className="ar-process-label">Funding Rate</div>
                  <div className="ar-process-val">
                    8h (native) → 1d (resampled)
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Access */}
          <div className="at-card">
            <div className="at-card-header">
              <span className="at-card-title">Access / Users</span>
            </div>
            <div className="at-card-body">
              <table className="ar-access-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Role</th>
                    <th>Access</th>
                    <th>Used By</th>
                  </tr>
                </thead>
                <tbody>
                  {USERS.map((u) => (
                    <tr key={u.name}>
                      <td className="ar-user-name">{u.name}</td>
                      <td>
                        <span
                          className={`ar-role-badge ${u.role.toLowerCase().replace('-', '')}`}
                        >
                          {u.role}
                        </span>
                      </td>
                      <td>{u.access}</td>
                      <td>{u.usage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="ar-access-note">
                ClickHouse accessible on port{' '}
                <span className="ar-mono">8123</span> (HTTP) and{' '}
                <span className="ar-mono">9000</span> (native TCP)
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
